import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const SITE_URL = 'https://www.patekarecycle.com';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  readTime: string;
  category: string;
  author: string;
  image?: string;
}

export function BlogDetail() {
  const { blogId } = useParams<{ blogId: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/data/blog.json')
      .then((res) => res.json())
      .then((data) => {
        const found = data.posts.find((p: BlogPost) => p.id === blogId);
        setPost(found || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [blogId]);

  if (loading) {
    return (
      <Layout>
        <div className="pt-28 pb-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-100 rounded w-24" />
              <div className="h-10 bg-gray-100 rounded w-3/4" />
              <div className="h-4 bg-gray-100 rounded w-48" />
              <div className="space-y-3 pt-8">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-100 rounded" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="pt-28 pb-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post not found</h1>
          <Link to="/blog" className="text-p-600 hover:underline text-sm">
            Back to Insights
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{post.title} | Punarvaa Insights</title>
        <meta name="title" content={`${post.title} | Punarvaa Insights`} />
        <meta name="description" content={post.excerpt} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/blog/${post.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${SITE_URL}/blog/${post.id}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
      </Helmet>

      {/* Article header */}
      <section className="pt-28 pb-10 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Insights
          </Link>

          <p className="text-xs font-semibold tracking-widest uppercase text-p-600 mb-4">
            {post.category}
          </p>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
            {post.title}
          </h1>

          <div className="flex items-center gap-5 text-sm text-gray-400">
            <span className="font-medium text-gray-600">{post.author}</span>
            <span>
              {new Date(post.date).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Article body */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          {post.content.startsWith('<') ? (
            <div
              className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-p-600 prose-li:text-gray-600"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          ) : (
            <div className="prose prose-lg max-w-none">
              {post.content.split('\n\n').map((para, i) => (
                <p key={i} className="text-gray-600 leading-relaxed mb-6">
                  {para}
                </p>
              ))}
            </div>
          )}

          {/* Back link */}
          <div className="mt-14 pt-8 border-t border-gray-100">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-p-600 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Insights
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-slate-50 border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ready to build a structured waste recovery system?
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Connect with our team to discuss your waste management needs.
          </p>
          <Link
            to="/contact?quote=true"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-p-600 text-white text-sm font-medium rounded-md hover:bg-p-700 transition-colors"
          >
            Get a Quote
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
